import { Ref } from 'vue';
import { CrowdHandlerWRState } from '../types/crowdhandler';
import { hoursUntilEvent } from '../utils/hoursUntilEvent';

export function createCountdown({ ch } : {
    ch: Ref<CrowdHandlerWRState>
}) {
    let countdownInterval: NodeJS.Timeout | null = null;
    let isRunning = false;
    let targetDate: Date | null = null;

    /**
     * Recalculates countdown from target date (handles tab throttling)
     */
    const recalculate = () => {
        if (!targetDate) return;

        const countdown = ch.value.countdown;
        if (!countdown) return;

        const now = new Date();
        const diff = targetDate.getTime() - now.getTime();

        // Check if countdown has completed
        if (diff <= 0) {
            countdown.totalseconds = -1;
            countdown.hours = 0;
            countdown.minutes = 0;
            countdown.seconds = 0;
            countdown.sHours = '00';
            countdown.sMinutes = '00';
            countdown.sSeconds = '00';
            cleanup();
            return;
        }

        const totalseconds = Math.floor(diff / 1000);
        const hours = Math.floor(totalseconds / 3600);
        const minutes = Math.floor((totalseconds % 3600) / 60);
        const seconds = Math.floor(totalseconds % 60);

        countdown.totalseconds = totalseconds;
        countdown.hours = hours;
        countdown.minutes = minutes;
        countdown.seconds = seconds;
        countdown.sHours = hours.toString().padStart(2, '0');
        countdown.sMinutes = minutes.toString().padStart(2, '0');
        countdown.sSeconds = seconds.toString().padStart(2, '0');
    };

    /**
     * Handle visibility change - recalculate immediately when tab becomes visible
     */
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && isRunning) {
            recalculate();
        }
    };

    const updateCountdown = (onsale?: string) => {
        // Get target date from parameter or ch.value.onsale
        const onsaleDate = onsale || ch.value.onsale;
        if (!onsaleDate) return;

        const newTarget = new Date(onsaleDate);

        // If already running with the same target, skip
        if (isRunning && targetDate && targetDate.getTime() === newTarget.getTime()) return;

        // Clean up existing interval before restarting
        if (countdownInterval) clearInterval(countdownInterval);

        targetDate = newTarget;
        isRunning = true;

        // Listen for tab visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Initial calculation
        recalculate();

        // Set up interval that recalculates each second
        countdownInterval = setInterval(recalculate, 1000);
    };

    /**
     * Cleans up the countdown interval and event listener
     */
    const cleanup = () => {
        if (countdownInterval) {
            clearInterval(countdownInterval);
            countdownInterval = null;
        }
        isRunning = false;
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    return {
        updateCountdown,
        cleanup,
        countdownInterval
    };
}


/**
 * Calculates countdown values based on target and current dates
 * @param {Date} targetDate - The target date to count down to
 * @param {Date} [currentDate=new Date()] - The current date (defaults to now)
 * @returns {Object} Object containing countdown values
 */
export const calculateCountdown = (onsale: string, currentDate: Date = new Date()): object | null => {
    const targetDate = new Date(onsale);
    const diff = targetDate.getTime() - currentDate.getTime();
    
    if (diff <= 0) {
        return null;
    }

    const totalseconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalseconds / 3600);
    const minutes = Math.floor((totalseconds % 3600) / 60);
    const seconds = Math.floor(totalseconds % 60);
    
    let sHours = hours.toString().padStart(2, '0');
    let sMinutes = minutes.toString().padStart(2, '0');
    let sSeconds = seconds.toString().padStart(2, '0');

    return {
        sHours,
        sMinutes,
        sSeconds,
        hours,
        minutes,
        seconds,
        totalseconds,
        inHours: hoursUntilEvent(targetDate, currentDate)
    };
};
